import React, { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useGlobalState } from "../../contexts/GlobalContext";
import { Routes } from "../../utils/constants";
import {
  fetchSessionValidateProxy,
} from "../../utils/helpers/api";
import { needsExternalSession } from "../../utils/helpers/string";
import { logger } from "../../utils/helpers/logger";

import Footer from "./Footer";
import Header from "./Header";
import Loader from "./Loader";
import Menu from "./Menu";
import { Modal } from "../Commons/Modal";
import { handleRedirect } from "../../utils/helpers/function";
import { useLayoutMenu } from "../../hooks/features/layout/useLayoutMenu";
import { useUrlParams } from "../../hooks/features/layout/useUrlParams";
import { useLayoutStorage } from "../../hooks/features/layout/useLayoutStorage";
import { useLayoutNavigation } from "../../hooks/features/layout/useLayoutNavigation";
import { useLayoutAuthentication } from "../../hooks/features/layout/useLayoutAuthentication";
import type { URLParams } from "../../hooks/features/layout/useUrlParams";

const Layout: React.FC = () => {
  const location = useLocation();
  const hasFetchedLatestUser = useRef(false); 
  const [urlParams, setUrlParams] = useState<URLParams>({
    authorizationCode: null,
    returnTo: null,
    mailMagId: null,
    transitionSource: null,
    surveyId: null,
    signUp: null,
    error: null,
    errorDescription: null,
    bulkDownload: null,
  })
  const navigate = useNavigate();
  const {
    setStateValue,
    globalState,
    refreshAccessToken,
    createExternalSession,
  } = useGlobalState();
  const calledHandleAuthProcess = useRef(false);

  // URLパラメーターを取得するロジック
  const { getUrlParams } = useUrlParams(location.search);
  // localStorageに保存するロジック
  const { setLocalStorage } = useLayoutStorage();
  // 認証状態やURLのパラメーターによってリダイレクトを行うロジック
  useLayoutNavigation({
    urlParams: {
      signUp: urlParams.signUp,
      surveyId: urlParams.surveyId,
      error: urlParams.error,
      errorDescription: urlParams.errorDescription,
      returnTo: urlParams.returnTo,
    }
  })
  // 認証やユーザー情報の更新を行うロジック
  const {
    handleAfterGetAuthorizationCode,
    authenticateInitialUser,
    updateTransitionSource,
  } = useLayoutAuthentication();
  // メニューの表示制御を行うロジック
  const {
    isShowMenu,
    setIsShowMenu,
    onMenuClick,
    shouldRenderMenu
  } = useLayoutMenu();

  // グローバルユーザーや現在のパスが変わるたびにチェックする一連の処理
  // WARNING: ロジックを整理したがまだ責務が多いのと、Layoutに持つべきロジックではないので改善必要
  const handleAuthProcess: () => Promise<void> = async () => {
    if (calledHandleAuthProcess.current) return;
    if (!globalState.user && urlParams.signUp === "true") {
      navigate(Routes.REGISTER);
      return;
    }
    logger("[Layout]", "handleAuthProcess executed", location.pathname, globalState.user)
    // 認証状態の更新
    // user_status = 4（メール未確認の仮登録状態）の場合は認証状態を更新しない
    // メール確認が完了してからuser_status = 5になり、その後に認証状態を更新する必要があるため
    const { user_metadata, email_verified } = globalState.user;
    const authenticated = user_metadata?.authenticated;
    const isNotAuthenticated = email_verified && !authenticated;
    if (isNotAuthenticated && user_metadata.user_status !== 4) {
      await authenticateInitialUser();
    }

    calledHandleAuthProcess.current = true;

    // リダイレクト処理
    const loginCallBackUrl = localStorage.getItem("loginCallBackUrl");
    const currentPath = location.pathname;
    const isOnSettingPage = currentPath === "/user/setting";
    const isLogin = localStorage.getItem("isLogin");
    const isGoogleLogin = localStorage.getItem("isGoogleLogin");
    const isSurveyMemberPage = currentPath.startsWith(Routes.SURVEY_MEMBER);
    const isSurveyMemberCallback = loginCallBackUrl?.includes(Routes.SURVEY_MEMBER);

    if (!loginCallBackUrl) return;
    if (isOnSettingPage) return;
    if (isSurveyMemberPage && isSurveyMemberCallback) {
      logger("[Layout]", "Current path is survey member and callback target is survey member, skipping global redirect");
      localStorage.removeItem("loginCallBackUrl");
      return;
    }
    if (isLogin) return;
    const checkValidate = await fetchSessionValidateProxy();
    if (isGoogleLogin || checkValidate) {
      if (urlParams.transitionSource) {
        await updateTransitionSource(urlParams.transitionSource);
      }

      localStorage.removeItem("transition_source");

      await handleRedirectPreparation(loginCallBackUrl, user_metadata.user_status ?? 0);
    };
  };

  // リダイレクトの準備処理
  // アクセストークンの更新、セッションの作成を行い、リダイレクトを実行する
  // 同一ドメインの場合はセッションIDを付与しない
  // 異なるドメインの場合はセッションIDを付与する
  const handleRedirectPreparation = async (returnTo: string, user_status: number, sessionId?: string) => {
    logger("[Layout]", "Preparing for redirect to:", returnTo, user_status);

    // WARNING: あんまり綺麗なやり方ではない
    // 新規登録後のユーザーがアンケートページに飛ばずuser/settingに飛ぶのでこのように実装
    const redirectToSurveyAnswerPage = returnTo.includes("/user/survey/answer");
    logger("[Layout]", "Is redirecting to survey answer page:", redirectToSurveyAnswerPage);
    if (redirectToSurveyAnswerPage) {
      logger("[Layout]", "Redirecting to survey answer page directly");
      // navigateを使うとuser/guestに飛ぶ可能性があるのでwindow.location.hrefを使う
      window.location.href = returnTo;
      localStorage.removeItem("loginCallBackUrl");
      return;
    }

    // 同一ドメインでも jpm.sendenkaigi.com は session_id を付与する
    const handleRedirectToOuterDomain = needsExternalSession(window.location.href, returnTo);
    if (!handleRedirectToOuterDomain) {
      logger("[Layout]", "Same domain, redirecting to", returnTo);
      return handleRedirect({ navigate, user_status, returnTo });
    }

    setStateValue("loader", true);
    try {
      const session_id = sessionId ?? await createExternalSession();
      handleRedirect({ navigate, user_status, returnTo, session_id: session_id || "" });
    } finally {
      setStateValue("loader", false);
    }
  };

  const mainContentClass = useMemo(() => {
    if (isShowMenu) {
      return "layout_main__content";
    }
    const baseClass = "wrapper";
    const isEventsPage = location.pathname.includes("/events");
    return isEventsPage ? `${baseClass} wrapper-events` : baseClass;

  }, [isShowMenu, location.pathname]);

  // globalUserとパスが変わったら毎回実行する一連の処理
  useEffect(() => {
    logger("[Layout]", location.pathname)
    if (!globalState.user) return;
    setStateValue("loader", false);
    handleAuthProcess();

    return () => {
      setStateValue("loader", false);
    }
  }, [globalState.user, location.pathname]);

  // パスが変わるたびに最新のユーザーを取得する
  useEffect(() => {
    if (!globalState.isAuthenticated) return;
    if (hasFetchedLatestUser.current) return;
    const fetchLatestUser = async () => {
      await refreshAccessToken();
      hasFetchedLatestUser.current = true;
    }
    fetchLatestUser();
  }, [location.pathname]);

  // Googleでログイン後にリダイレクトする時にAuthorizationCodeがURLに含まれるので
  // それを検知して処理を行う
  useEffect(() => {
    if (!urlParams.authorizationCode) return;
    handleAfterGetAuthorizationCode(urlParams.authorizationCode, async (params: {
      sessionId?: string,
      userStatus?: number
    }) => {
      const survey_url = localStorage.getItem("survey_url");
      const loginCallBackUrl = localStorage.getItem("loginCallBackUrl");
      logger("[Layout]", "Authorization code", survey_url, loginCallBackUrl);

      if (survey_url) {
        navigate(survey_url);
        localStorage.removeItem("survey_url");
        localStorage.removeItem("loginCallBackUrl");
      } else if (!loginCallBackUrl) {
        navigate(Routes.USERSETTING);
      } else {
        const { sessionId, userStatus } = params;
        await handleRedirectPreparation(loginCallBackUrl, userStatus ?? 0, sessionId);
        localStorage.removeItem("loginCallBackUrl");
      }
      localStorage.removeItem("hasVisitedVerifyEmail");
      localStorage.setItem("settingGoogle", "true");
    });
  }, [urlParams.authorizationCode]);


  useEffect(() => {
    const params = getUrlParams();
    setUrlParams(params);
    const urlParams = getUrlParams();
    // URLのクエリパラメーターを取得してlocalStorageに保存
    setUrlParams(urlParams);
    setLocalStorage({
      urlParams: {
        returnTo: urlParams.returnTo,
        mailMagId: urlParams.mailMagId,
        transitionSource: urlParams.transitionSource,
        surveyId: urlParams.surveyId,
        bulkDownload: urlParams.bulkDownload,
      }
    });

    return () => {
      setUrlParams({
        authorizationCode: null,
        returnTo: null,
        mailMagId: null,
        transitionSource: null,
        surveyId: null,
        signUp: null,
        error: null,
        errorDescription: null,
        bulkDownload: null,
      })
    }
  }, [location.search]);

  // Layoutの初期化処理
  useEffect(() => {
    logger("[Layout]", "Mounted", location.pathname);
    () => {
      if (globalState.loader) {
        logger("[Layout]", "Unmounted, turning off loader");
        // ローディングが表示されっぱなしを防止するための保険
        setStateValue("loader", false);
      }
    }
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Loader />
      <Modal />
      <Header onMenuClick={onMenuClick} isAuthenticated={globalState.isAuthenticated} setIsShowMenu={setIsShowMenu}/>
      <main id="main" className="layout_main">
        <div className={mainContentClass}>
          {shouldRenderMenu && (
            <Menu
              isShowMenu={isShowMenu}
              onClickMenuItem={onMenuClick}
              setIsShowMenu={setIsShowMenu}
            />
          )}
          <div
            className={
              location.pathname.startsWith(Routes.Events)
                ? ""
                : "layout_main__content__screen"
            }
          >
            <Outlet />
          </div>
        </div>
      </main>
      <Footer isAuthenticated={isShowMenu && globalState.isAuthenticated} />
    </div>
  );
};

export default Layout;
